/**
 * LA PALETTE DU PLAN — UNE SEULE SOURCE POUR LA CARTE ET SA LÉGENDE.
 *
 * ⚠️ ELLES DIVERGEAIENT. Le style MapLibre portait ses couleurs en dur, et la
 * légende en dessous les retapait à la main : douze littéraux dupliqués, qui
 * n'avaient aucun moyen de rester d'accord. Une légende qui ment sur la carte
 * est pire qu'une absence de légende — elle est crue.
 *
 * ⚠️ MapLibre et `<canvas>` n'acceptent pas `var()` : on lit donc les jetons au
 * moment de construire. Le crochet `useThemeActif()` remet l'effet en marche
 * quand le thème change, et les valeurs sont relues.
 *
 * ⚠️ LA TEINTE EST RÉSERVÉE À L'ÉTAT D'UNE PARCELLE. La hiérarchie du plan —
 * divisions, rangs de voie, volumes — se dit par la VALEUR, du plus sourd au
 * plus clair. Un plan cadastral où la voirie serait bleue et les îlots verts
 * rendrait illisible le seul signal qui compte : ce qui ne va pas.
 */
const lire = (nom: string, repli: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(nom).trim() || repli;

export function paletteCarte() {
  const p = {
    fond: lire('--carte-fond', '#000000'),
    eau: lire('--carte-eau', '#0a0a0a'),
    halo: lire('--carte-halo', '#000000'),
    trait: lire('--carte-trait', '#2b2b2b'),
    etiquette: lire('--carte-etiquette', '#d0d0d0'),
    etiquetteFaible: lire('--carte-etiquette-faible', '#8a8a8a'),
    /* ── DÉCOUPAGE — une seule famille, trois niveaux de valeur. */
    decoupage: lire('--carte-decoupage', '#2fa3ba'),
    division: [lire('--carte-division-1', '#48b2c6'), lire('--carte-division-2', '#268a9d'),
               lire('--carte-division-3', '#1c6a79')] as const,
    section: lire('--carte-section', '#55bccd'),
    /**
     * ⚠️ LA COLLECTIVITÉ EST LE TRAIT LE PLUS LOURD DU PLAN, et c'est une
     * règle de lecture, pas de goût : elle est la limite dans laquelle un code
     * de section devient unique. Mesuré le 21/09 : 1 515 codes sur 1 552 sont
     * portés par PLUSIEURS collectivités. Dessiner ce cadre du même trait
     * qu'une limite d'orientation ferait lire « AAA » comme un identifiant.
     */
    collectivite: lire('--carte-collectivite', '#86dcec'),

    /* ── BÂTI — la famille, puis ce qu'on en SAIT.
       ⚠️ `bati` et `batiNon` s'appelaient `parcelleOui`/`parcelleNon`, et le nom
       mentait : la légende les range sous « Bâti — rattaché / non rattaché ».
       Un nom qui désigne autre chose que son objet se recopie de bonne foi. */
    bati: lire('--carte-bati', '#b06ba4'),
    /**
     * ⚠️⚠️ DERIVE, PLUS JAMAIS UNE SECONDE VALEUR A MAINTENIR. `--carte-bati`
     * et `--carte-bati-non` valaient #b06ba4 et #a25f96 : ecart de teinte
     * 1 DEGRE, contraste mutuel 1,19 — la MEME couleur. La distinction ne
     * tenait qu'au tiret du contour, et `batiments-contour-presume` porte
     * `minzoom: 14` : sous z14 elle n'existait pas du tout. Un lavis a 22 %
     * oppose un CORPS a un VIDE, ce qui tient a tous les zooms, et une valeur
     * derivee ne peut pas diverger de sa source.
     */
    batiNon: '',   // rempli juste apres, une fois `bati` lu
    batiTrait: lire('--carte-bati-trait', '#c489ba'),
    volumeReleve: lire('--carte-volume-releve', '#bd7fb1'),
    volumePresume: lire('--carte-volume-presume', '#7d5175'),
    volumeMur: lire('--carte-volume-mur', '#a2688f'),

    /* ── VOIRIE — rampe de VALEUR et non de teinte : elle est ORDONNÉE
       (structurante > desserte > locale), et une rampe ordonne mieux qu'une
       teinte. Le fondateur ne contestait pas la rampe, il constatait que quatre
       CATÉGORIES ne s'ordonnent pas. */
    voie: [lire('--carte-voie-1', '#6e6e6e'), lire('--carte-voie-2', '#4a4a4a'),
           lire('--carte-voie-3', '#333333')] as const,
    maille: lire('--carte-maille', '#3f3f3f'),
    choix: lire('--carte-choix', '#ffffff'),
    /** Sept natures d'équipement — la SEULE dimension catégorielle du produit. */
    equipement: {
      education: lire('--cat-education', '#3b6ea5'),
      sante: lire('--cat-sante', '#a8443a'),
      commerce: lire('--cat-commerce', '#8a6a2f'),
      espace_vert: lire('--cat-espace-vert', '#3d7a4e'),
      sport: lire('--cat-sport', '#63459c'),
      administration: lire('--cat-administration', '#5e6a74'),
      securite: lire('--cat-securite', '#96406b'),
      autre: lire('--cat-autre', '#6e6e6e'),
    },
    /** Les quatre états d'une parcelle — les seules TEINTES du plan. */
    /**
     * ⚠️⚠️ LES ETATS AVAIENT LES JETONS D'INTERFACE, ET C'ETAIT LA CAUSE.
     * `--vert`, `--violet`, `--rouge` sont les couleurs des pastilles de
     * formulaire : chroma 0,17 a 0,247, dessinee pour un badge de 12 px, pas
     * pour des aplats de parcellaire. Et surtout elles n'ont AUCUNE raison de
     * rester separees entre elles : mesure du 22/09, pire paire 5,0 dE en
     * sombre et 6,1 en clair sous les trois dichromacies — sous le seuil de 12.
     * L'ocre remplace le violet parce que le violet etait a 40 degres du bati.
     * La palette du plan a donc ses propres jetons, verifies : pire paire
     * 13,6 dE en sombre, 18,1 en clair. Garde : tests/palette-etats.test.ts.
     */
    statut: {
      active: lire('--carte-etat-inscrite', '#5cb572'),
      enInstruction: lire('--carte-etat-instruction', '#f0b13f'),
      suspendue: lire('--carte-etat-suspendue', '#ba3e3e'),
      retiree: lire('--carte-maille', '#6c6c6c'),
    },
  };
  // Le lavis se compose sur le FOND : un meme alpha ne donne pas la meme
  // couleur sur du noir et sur du blanc, et MapLibre n'aplatit pas pour nous.
  p.batiNon = sur(p.bati, 0.22, p.fond);
  return p;
}
/**
 * `<canvas>` non plus ne lit pas `var()`. Il accepte en revanche `#rrggbbaa`,
 * ce qui permet de composer une transparence sur un jeton sans le recopier.
 */
/** Compose une couleur a `a` d'opacite SUR un fond opaque, et rend un opaque. */
export const sur = (couleur: string, a: number, fond: string): string => {
  const px = (h: string): number[] => {
    const c = h.trim().replace('#', '');
    const plein = c.length === 3 ? c.split('').map((x) => x + x).join('') : c.slice(0, 6);
    return [0, 2, 4].map((i) => parseInt(plein.slice(i, i + 2), 16));
  };
  const A = px(couleur);
  const B = px(fond);
  return '#' + A.map((v, i) => Math.round(v * a + (B[i] ?? 0) * (1 - a))
    .toString(16).padStart(2, '0')).join('');
};

export const alpha = (couleur: string, a: number): string => {
  const h = couleur.trim().replace('#', '');
  const plein = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  const o = Math.round(Math.min(1, Math.max(0, a)) * 255).toString(16).padStart(2, '0');
  return `#${plein}${o}`;
};

export type PaletteCarte = ReturnType<typeof paletteCarte>;
